import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Sparkles, Zap, Command, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import FeedbackButton from '../components/FeedbackButton';
import MainNavigation from '../components/MainNavigation';
import ChatSidebar from '../components/chat/ChatSidebar';
import ChatHeader from '../components/chat/ChatHeader';
import ChatInput from '../components/chat/ChatInput';
import MessageBubble from '../components/chat/MessageBubble';
import { TokenLimitDialog, UsageGuideModal } from '../components/chat/ChatDialogs';
import CommandPalette from '../components/signature/CommandPalette';
import ThinkingOrb from '../components/signature/ThinkingOrb';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Create = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  // ─── State ───
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [showTokenLimitDialog, setShowTokenLimitDialog] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [enableUltra, setEnableUltra] = useState(false);
  const [subagents, setSubagents] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [showUsageGuide, setShowUsageGuide] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // ─── Refs ───
  const renameInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const streamReaderRef = useRef(null);
  const isStreamingRef = useRef(false);
  const currentAgentIdRef = useRef(null);
  const streamingConversationIdRef = useRef(null);
  const generatingStatusRef = useRef({});
  const hasLoadedConversationsRef = useRef(false);

  // ─── Effects ───
  useEffect(() => { fetchAgents(); fetchConversations(); }, []);

  useEffect(() => {
    // Only poll when there's an active generation (not the current stream) to detect background completions
    const hasBackgroundGeneration = conversations.some(c => c.is_generating && c.id !== streamingConversationIdRef.current);
    if (!hasBackgroundGeneration) return;
    const interval = setInterval(() => { fetchConversations(true); }, 10000);
    return () => clearInterval(interval);
  }, [currentConversation?.id, conversations]);

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    if (!selectedAgent || currentConversation) return;
    if (isStreamingRef.current && currentAgentIdRef.current && currentAgentIdRef.current !== selectedAgent.id) {
      if (streamReaderRef.current) { try { streamReaderRef.current.cancel(); } catch {} }
      isStreamingRef.current = false;
      streamReaderRef.current = null;
      setLoading(false);
    }
    currentAgentIdRef.current = selectedAgent.id;
    if (messages.length === 0 || (isStreamingRef.current === false && !currentConversation)) {
      if (selectedAgent.greeting_message) {
        setMessages([{ role: 'assistant', content: selectedAgent.greeting_message, timestamp: new Date().toISOString(), isGreeting: true }]);
      }
    }
  }, [selectedAgent?.id, currentConversation]);

  useEffect(() => { fetchSubagents(selectedAgentId); }, [selectedAgentId]);

  useEffect(() => {
    if (textareaRef.current) {
      const isMobile = window.innerWidth < 768;
      const maxHeight = isMobile ? 120 : 200;
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, maxHeight);
      textareaRef.current.style.height = newHeight + 'px';
      textareaRef.current.style.overflowY = textareaRef.current.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }
  }, [input]);

  useEffect(() => {
    const checkMobile = () => { if (window.innerWidth < 768) setSidebarCollapsed(true); };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ─── Helpers ───
  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); };

  const cancelActiveStream = () => {
    if (streamReaderRef.current) { try { streamReaderRef.current.cancel(); } catch {} }
    isStreamingRef.current = false;
    streamReaderRef.current = null;
    streamingConversationIdRef.current = null;
    setLoading(false);
  };

  const stopGeneration = async () => {
    const convId = currentConversation?.id || streamingConversationIdRef.current;
    cancelActiveStream();

    // ✅ Immediately reset local flags so input is usable + mark this conv as not generating
    setLoading(false);
    if (convId) {
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, is_generating: false } : c));
      if (streamingConversationIdRef.current === convId) {
        streamingConversationIdRef.current = null;
      }
    }

    // Mark last message as finished
    setMessages(prev => {
      const m = [...prev];
      if (m.length > 0 && m[m.length - 1].role === 'assistant') {
        m[m.length - 1].isStreaming = false;
        m[m.length - 1].isThinking = false;
        m[m.length - 1].isUltraGenerating = false;
        m[m.length - 1].isProcessing = false;
      }
      return m;
    });

    // Tell server to stop (fire and forget — don't block UI)
    if (convId) {
      axios.post(`${API_URL}/api/chat/conversations/${convId}/stop`).catch(() => {});
    }
    toast.info('已停止生成');
  };

  // ─── Data Fetching ───
  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/agents`);
      const userRole = user?.role || 'user';
      const filteredAgents = response.data.filter(agent => {
        if (userRole === 'admin' || userRole === 'operator') return true;
        return (agent.allowed_roles || ['vip', 'admin']).includes(userRole);
      });
      setAgents(filteredAgents);
      if (filteredAgents.length > 0 && !selectedAgent) {
        const first = filteredAgents[0];
        setSelectedAgent(first);
        setSelectedAgentId(first.id);
        if (first.greeting_message) {
          setMessages([{ role: 'assistant', content: first.greeting_message, timestamp: new Date().toISOString(), isGreeting: true }]);
        }
      }
      if (filteredAgents.length === 0 && response.data.length > 0) toast.info('升級為 VIP 用戶以使用更多 Agent！');
    } catch { toast.error('獲取Agent失敗'); }
  };

  const fetchSubagents = async (agentId) => {
    if (!agentId) { setSubagents([]); return; }
    try { const res = await axios.get(`${API_URL}/api/agents/${agentId}/subagents`); setSubagents(res.data || []); }
    catch { setSubagents([]); }
  };

  const fetchConversations = async (shouldNotify = false) => {
    try {
      const response = await axios.get(`${API_URL}/api/chat/conversations`);
      const nextConversations = response.data || [];
      if (shouldNotify && hasLoadedConversationsRef.current) {
        const previousStatus = generatingStatusRef.current || {};
        nextConversations.forEach((conv) => {
          if (previousStatus[conv.id] && !conv.is_generating) {
            if (conv.last_generation_summary) {
              const s = conv.last_generation_summary;
              toast.success(`「${conv.agent_name}」回覆已完成${s.ultra_mode ? ' (Ultra)' : ''}！消耗 ${Number(s.credits_charged || 0).toFixed(2)} 點，剩餘 ${Number(s.credits_remaining || 0).toFixed(2)} 點`, { duration: 5000 });
            } else { toast.success(`「${conv.agent_name}」回覆已完成`, { duration: 4000 }); }
            if (currentConversation?.id === conv.id && streamingConversationIdRef.current !== conv.id) loadConversation(conv.id);
          }
        });
      }
      // Only update state if data actually changed (prevents re-render that deselects highlighted text)
      setConversations(prev => {
        const prevKey = prev.map(c => `${c.id}:${c.is_generating}:${c.updated_at}`).join('|');
        const nextKey = nextConversations.map(c => `${c.id}:${c.is_generating}:${c.updated_at}`).join('|');
        return prevKey === nextKey ? prev : nextConversations;
      });
      generatingStatusRef.current = nextConversations.reduce((acc, c) => { acc[c.id] = !!c.is_generating; return acc; }, {});
      if (!hasLoadedConversationsRef.current) hasLoadedConversationsRef.current = true;
    } catch {}
  };

  // ─── Conversation Actions ───
  const deleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    if (!window.confirm('確定要刪除這個對話嗎？')) return;
    try {
      await axios.delete(`${API_URL}/api/chat/conversations/${conversationId}`);
      toast.success('對話已刪除');
      if (currentConversation?.id === conversationId) { setCurrentConversation(null); setMessages([]); }
      fetchConversations();
    } catch { toast.error('刪除對話失敗'); }
  };

  const startRename = (conv, e) => {
    e.stopPropagation();
    setRenamingId(conv.id);
    setRenameValue(conv.custom_name || conv.agent_name);
    setTimeout(() => renameInputRef.current?.select(), 50);
  };

  const submitRename = async (convId) => {
    const name = renameValue.trim();
    if (!name) { setRenamingId(null); return; }
    try {
      await axios.put(`${API_URL}/api/chat/conversations/${convId}/rename`, { name });
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, custom_name: name } : c));
      toast.success('已重命名');
    } catch { toast.error('重命名失敗'); }
    setRenamingId(null);
  };

  const loadConversation = async (conversationId) => {
    try {
      if (streamingConversationIdRef.current && streamingConversationIdRef.current !== conversationId) cancelActiveStream();
      const response = await axios.get(`${API_URL}/api/chat/conversations/${conversationId}`);
      setCurrentConversation(response.data);
      setMessages(response.data.messages || []);
      const agent = agents.find(a => a.id === response.data.agent_id);
      if (agent) { setSelectedAgent(agent); setSelectedAgentId(agent.id); }
      if (response.data?.is_generating) subscribeToConversationStream(response.data.id, response.data.agent_id);
    } catch { toast.error('載入對話失敗'); }
  };

  const startNewConversation = () => {
    cancelActiveStream();
    setCurrentConversation(null);
    setTokenInfo(null);
    setMessages(() => {
      if (selectedAgent?.greeting_message) return [{ role: 'assistant', content: selectedAgent.greeting_message, timestamp: new Date().toISOString(), isGreeting: true }];
      return [];
    });
  };

  const handleAgentChange = (value) => {
    const agent = agents.find(a => a.id === value);
    if (!agent) return;
    setTimeout(() => fetchConversations(), 1500);
    setSelectedAgent(agent);
    setSelectedAgentId(value);
    setCurrentConversation(null);
    setTokenInfo(null);
    setMessages([]);
  };

  // ─── Stream event handler (shared by both subscribe and send) ───
  const handleStreamEvent = (data, conversationId, shouldUpdate) => {
    if (data.type === 'subagent_start') {
      if (!shouldUpdate()) return;
      setMessages(prev => { const m = [...prev]; const l = m.length - 1; if (l >= 0 && m[l]) { m[l] = { ...m[l], subagent_id: data.subagent_id, subagent_name: data.subagent_name, subagent_color: data.subagent_color }; } return m; });
    } else if (data.type === 'progress') {
      if (!shouldUpdate()) return;
      setMessages(prev => { const m = [...prev]; const l = m.length - 1; if (l >= 0 && m[l]) { m[l] = { ...m[l], progress: data.content, isProcessing: true }; } return m; });
    } else if (data.type === 'ultra_status') {
      if (!shouldUpdate()) return;
      setMessages(prev => { const m = [...prev]; const l = m.length - 1; if (l >= 0 && m[l]) { m[l] = { ...m[l], ultraStatus: data.step, isUltraGenerating: true }; } return m; });
    } else if (data.type === 'review_progress' || data.type === 'review_start' || data.type === 'review_end') {
      if (!shouldUpdate()) return;
      setMessages(prev => { const m = [...prev]; const l = m.length - 1; if (l >= 0 && m[l]) { m[l] = { ...m[l], reviewProgress: data.content, isReviewing: data.type !== 'review_end', isUltraGenerating: false }; } return m; });
    } else if (data.type === 'reasoning') {
      if (!shouldUpdate()) return;
      setMessages(prev => { const m = [...prev]; const l = m.length - 1; if (l >= 0 && m[l]) { m[l] = { ...m[l], reasoning: (m[l].reasoning || '') + data.content, isThinking: true, isProcessing: false }; } return m; });
    } else if (data.type === 'content') {
      if (!shouldUpdate()) return;
      setMessages(prev => { const m = [...prev]; const l = m.length - 1; if (l >= 0 && m[l]) { m[l] = { ...m[l], content: (m[l].content || '') + data.content, isThinking: false, isUltraGenerating: false }; } return m; });
    } else if (data.type === 'review_feedback') {
      if (!shouldUpdate()) return;
      setMessages(prev => { const m = [...prev]; const l = m.length - 1; if (l >= 0 && m[l]) { m[l] = { ...m[l], reviewFeedback: (m[l].reviewFeedback || '') + data.content, isReviewing: true }; } return m; });
    } else if (data.type === 'done') {
      return 'done';
    } else if (data.type === 'error') {
      return 'error';
    }
    return null;
  };

  const handleDoneEvent = async (data, conversationId) => {
    setMessages(prev => {
      const m = [...prev];
      if (m.length > 0) {
        const l = m.length - 1;
        m[l].isStreaming = false; m[l].isThinking = false; m[l].isReviewing = false; m[l].isUltraGenerating = false;
        if (data.ultra_mode) m[l].wasOptimized = true;
        if (data.subagent_id) { m[l].subagent_id = data.subagent_id; m[l].subagent_name = data.subagent_name; m[l].subagent_color = data.subagent_color; }
      }
      return [...m];
    });
    generatingStatusRef.current[data.conversation_id || conversationId] = false;
    setConversations(prev => prev.map(c => c.id === (data.conversation_id || conversationId) ? { ...c, is_generating: false } : c));
    if (data.token_info) {
      setTokenInfo(data.token_info);
      if (data.token_info.approaching_limit) toast.warning(`對話即將達到上限（${Math.round(data.token_info.current / data.token_info.max * 100)}%），建議新建對話`);
    }
    await refreshUser();
    fetchConversations();

    // ✨ AI auto-title: trigger after first AI response completes (fire and forget)
    const targetConvId = data.conversation_id || conversationId;
    try {
      const existing = conversations.find(c => c.id === targetConvId);
      // Only generate if there's no custom_name yet
      if (targetConvId && !existing?.custom_name) {
        axios.post(`${API_URL}/api/chat/conversations/${targetConvId}/auto-title`)
          .then(() => fetchConversations())
          .catch(() => {});
      }
    } catch {}

    const cc = data.credits_charged || 0;
    if (cc > 0) toast.success(`生成成功${data.ultra_mode ? ' (Ultra)' : ''}！消耗 ${cc.toFixed(2)} 點，剩餘 ${data.credits_remaining.toFixed(2)} 點`, { duration: 4000 });
    else toast.success(`生成成功！剩餘 ${data.credits_remaining.toFixed(2)} 點`);
  };

  // ─── Subscribe to existing stream (with auto-reconnect) ───
  const subscribeToConversationStream = async (conversationId, agentId) => {
    // Don't cancel if we're reconnecting to the same conversation
    if (streamingConversationIdRef.current && streamingConversationIdRef.current !== conversationId) {
      cancelActiveStream();
    }
    streamingConversationIdRef.current = conversationId;
    currentAgentIdRef.current = agentId;
    isStreamingRef.current = true;
    setLoading(true);

    setMessages(prev => {
      const last = prev[prev.length - 1];
      if (!last || last.role !== 'assistant' || !last.isStreaming) {
        return [...prev, { role: 'assistant', content: '', reasoning: '', timestamp: new Date().toISOString(), isStreaming: true, isThinking: false }];
      }
      return prev;
    });

    let receivedDone = false;
    try {
      const response = await fetch(`${API_URL}/api/chat/stream/${conversationId}`, { method: 'GET', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      if (!response.ok) { setLoading(false); isStreamingRef.current = false; return; }
      const reader = response.body.getReader();
      streamReaderRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';
      const shouldUpdate = () => streamingConversationIdRef.current === conversationId;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim() || !line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (!shouldUpdate()) continue;
            if (data.type === 'sync') {
              setMessages(prev => { const m = [...prev]; const l = m.length - 1; if (l >= 0 && m[l]) { m[l] = { ...m[l], content: data.content || m[l].content, reasoning: data.reasoning || m[l].reasoning, isStreaming: true, isThinking: !!data.reasoning }; } return m; });
              continue;
            }
            const result = handleStreamEvent(data, conversationId, shouldUpdate);
            if (result === 'done') { receivedDone = true; await handleDoneEvent(data, conversationId); break; }
            if (result === 'error') { receivedDone = true; toast.error(data.message || '生成失敗'); break; }
          } catch {}
        }
        if (receivedDone) break;
      }
    } catch (e) {
      console.log('[Subscribe] Stream error:', e.message);
    }

    streamReaderRef.current = null;

    // Auto-reconnect if connection dropped without done/error
    if (!receivedDone && streamingConversationIdRef.current === conversationId) {
      console.log('[Subscribe] Connection dropped at ~70s, auto-reconnecting...');
      await new Promise(r => setTimeout(r, 300));
      // Check if generation is still active on server
      try {
        const checkResp = await axios.get(`${API_URL}/api/chat/conversations/${conversationId}`);
        if (checkResp.data?.is_generating) {
          subscribeToConversationStream(conversationId, agentId);
          return; // Don't clean up - reconnected stream takes over
        }
      } catch {}
      // Generation finished while we were disconnected - load full conversation
      await loadConversation(conversationId);
    }

    // Clean up only if we're not reconnecting
    if (streamingConversationIdRef.current === conversationId) {
      streamingConversationIdRef.current = null;
    }
    isStreamingRef.current = false;
    setLoading(false);
  };

  // ─── Send message ───
  const sendMessage = async () => { if (!input.trim() || !selectedAgent) return; await sendMessageWithContent(input); };

  const sendMessageWithContent = async (messageContent) => {
    if (!messageContent.trim() || !selectedAgent) return;
    const processingAgentId = selectedAgent.id;
    currentAgentIdRef.current = processingAgentId;
    const processingConversationId = currentConversation?.id || (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    streamingConversationIdRef.current = processingConversationId;
    generatingStatusRef.current[processingConversationId] = true;
    if (!currentConversation?.id) setCurrentConversation({ id: processingConversationId, agent_id: selectedAgent.id, agent_name: selectedAgent.name, messages: [] });
    setConversations(prev => {
      const exists = prev.some(c => c.id === processingConversationId);
      const updated = prev.map(c => c.id === processingConversationId ? { ...c, is_generating: true, updated_at: new Date().toISOString() } : c);
      if (!exists) return [{ id: processingConversationId, agent_id: selectedAgent.id, agent_name: selectedAgent.name, updated_at: new Date().toISOString(), is_generating: true, messages: [] }, ...updated];
      return updated;
    });
    const shouldUpdate = () => currentAgentIdRef.current === processingAgentId && streamingConversationIdRef.current === processingConversationId;
    const userMessage = { role: 'user', content: messageContent, timestamp: new Date().toISOString() };
    const mentionMatch = messageContent.match(/^@([\w\u4e00-\u9fff\u3400-\u4dbf]+)\s*/);
    if (mentionMatch) { const mentioned = subagents.find(sa => sa.name === mentionMatch[1]); if (mentioned) userMessage.mentioned_subagent_name = mentioned.name; }
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    let receivedDone = false;
    try {
      const response = await fetch(`${API_URL}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ agent_id: selectedAgent.id, message: messageContent, conversation_id: processingConversationId, enable_ultra: enableUltra })
      });
      if (response.status === 429) { setShowTokenLimitDialog(true); setMessages(prev => prev.slice(0, -1)); setLoading(false); return; }
      if (response.status === 402) { setMessages(prev => prev.slice(0, -1)); setLoading(false); toast.error('點數不足'); toast.info('點擊右上角點數可前往充值'); return; }
      if (response.status === 409) { setMessages(prev => prev.slice(0, -1)); setLoading(false); toast.info('此對話正在生成中'); return; }
      if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.detail || `請求失敗 (${response.status})`); }

      const reader = response.body.getReader();
      streamReaderRef.current = reader;
      isStreamingRef.current = true;
      const decoder = new TextDecoder();
      setMessages(prev => [...prev, { role: 'assistant', content: '', reasoning: '', timestamp: new Date().toISOString(), isStreaming: true, isThinking: false, isUltraGenerating: enableUltra }]);
      let buffer = '';

      while (true) {
        if (!isStreamingRef.current) break;
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6).trim());
              if (!data) continue;
              const result = handleStreamEvent(data, processingConversationId, shouldUpdate);
              if (result === 'done') {
                receivedDone = true;
                if (shouldUpdate()) {
                  if (!currentConversation) setCurrentConversation({ id: data.conversation_id });
                  await handleDoneEvent(data, processingConversationId);
                }
              } else if (result === 'error') {
                receivedDone = true;
                toast.error(data.message || '生成失敗');
              }
            } catch {}
          }
        }
        if (receivedDone) break;
      }
    } catch (error) {
      toast.error(error.message || '發送失敗');
      setMessages(prev => prev.slice(0, -1));
      generatingStatusRef.current[processingConversationId] = false;
      setConversations(prev => prev.map(c => c.id === processingConversationId ? { ...c, is_generating: false } : c));
      setLoading(false);
      isStreamingRef.current = false;
      streamReaderRef.current = null;
      streamingConversationIdRef.current = null;
      return;
    }

    streamReaderRef.current = null;

    // Auto-reconnect: if stream ended without done/error (~70s proxy timeout)
    if (!receivedDone && shouldUpdate()) {
      console.log('[Send] Connection dropped without done, auto-reconnecting via /stream/...');
      // subscribeToConversationStream handles its own cleanup
      subscribeToConversationStream(processingConversationId, processingAgentId);
      return;
    }

    // Normal cleanup
    if (streamingConversationIdRef.current === processingConversationId) {
      streamingConversationIdRef.current = null;
    }
    isStreamingRef.current = false;
    setLoading(false);
  };

  if (!user) return null;

  const currentConversationIsGenerating = !!conversations.find(c => c.id === currentConversation?.id)?.is_generating;

  // ─── Render ───
  return (
    <div
      data-theme="dark"
      data-atelier="true"
      className="h-screen flex overflow-hidden surface-obsidian pb-14 md:pb-0"
    >
      <MainNavigation />

      <ChatSidebar
        collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}
        conversations={conversations} currentConversation={currentConversation}
        renamingId={renamingId} renameValue={renameValue} setRenameValue={setRenameValue} renameInputRef={renameInputRef}
        onNewConversation={startNewConversation} onLoadConversation={loadConversation}
        onDeleteConversation={deleteConversation} onStartRename={startRename}
        onSubmitRename={submitRename} onCancelRename={() => setRenamingId(null)}
      />

      <div className="flex-1 flex flex-col min-w-0 bg-obsidian-900 grain-dark relative">
        {/* Top brand neon strip — matches Landing */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-lime-500/60 to-transparent z-30 pointer-events-none" />

        <ChatHeader
          sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}
          agents={agents} selectedAgent={selectedAgent} selectedAgentId={selectedAgentId}
          onAgentChange={handleAgentChange} tokenInfo={tokenInfo} currentConversation={currentConversation}
          user={user} onLogout={logout} onProfile={() => navigate('/profile')}
          onShowUsageGuide={() => setShowUsageGuide(true)}
        />

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto chat-scroll">
          <div className={`${messages.length === 0 ? 'max-w-5xl' : 'max-w-[900px]'} mx-auto px-4 md:px-10 py-8 md:py-12`}>
            {messages.length === 0 ? (
              <div className="py-12 md:py-20">
                <div className="flex flex-col items-center text-center max-w-2xl mx-auto">
                  <ThinkingOrb size={120} label="AI · READY" />

                  <div className="mt-10 mb-5 flex items-center justify-center gap-3">
                    <span className="h-px w-8 bg-atelier-lineHi" />
                    <span className="atelier-caption">Studio · Open</span>
                    <span className="h-px w-8 bg-atelier-lineHi" />
                  </div>

                  <h2
                    className="text-4xl md:text-5xl mb-4 text-atelier-ink tracking-[-0.02em] leading-[1.05]"
                    style={{
                      fontFamily: "'Noto Serif TC', 'Playfair Display', Georgia, serif",
                      fontWeight: 500,
                    }}
                  >
                    {selectedAgent?.name || '選擇 Agent 開始'}
                  </h2>
                  <p className="text-atelier-mute text-sm md:text-base max-w-md leading-[1.85]">
                    {selectedAgent?.description || '從上方選擇一個 AI 助理'}
                  </p>

                  {/* Hint chips */}
                  <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
                    <div className="inline-flex items-center gap-2 px-3 h-8 rounded-full border border-atelier-line bg-atelier-surface text-xs text-atelier-mute">
                      <Sparkles className="w-3 h-3 text-atelier-coral" strokeWidth={1.8} />
                      <span>輸入訊息直接開始對話</span>
                    </div>
                    <div className="hidden md:inline-flex items-center gap-2 px-3 h-8 rounded-full border border-atelier-line bg-atelier-surface text-xs text-atelier-mute">
                      <Command className="w-3 h-3" strokeWidth={1.6} />
                      <span>按</span>
                      <kbd className="px-1.5 py-0.5 bg-atelier-surface2 rounded text-[10px] font-mono border border-atelier-line text-atelier-ink">⌘K</kbd>
                      <span>快速切換</span>
                    </div>
                    {selectedAgent?.review_config?.review_enabled && (
                      <div className="inline-flex items-center gap-2 px-3 h-8 rounded-full border border-atelier-coral/40 bg-atelier-coral/10 text-xs text-atelier-coral">
                        <Zap className="w-3 h-3" fill="currentColor" />
                        <span>此 Agent 支援 Ultra 模式</span>
                      </div>
                    )}
                  </div>

                  {/* Quick prompt suggestions */}
                  {selectedAgent && (
                    <div className="mt-12 w-full grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
                      {[
                        { label: '開始創作爆款腳本', q: '幫我生成一個關於職場成長的 Reels 腳本' },
                        { label: '分析一個參考影片', q: '幫我分析這個影片的爆款邏輯：[影片連結]' },
                      ].map((s, i) => (
                        <button
                          key={i}
                          onClick={() => setInput(s.q)}
                          className="group atelier-card p-4 hover:border-atelier-coral/50 transition-all text-left"
                          data-testid={`quick-prompt-${i}`}
                        >
                          <div className="atelier-caption mb-2">SUGGESTION · {String(i + 1).padStart(2, '0')}</div>
                          <div
                            className="text-[15px] text-atelier-ink mb-2 tracking-tight leading-snug"
                            style={{
                              fontFamily: "'Noto Serif TC', 'Playfair Display', Georgia, serif",
                              fontWeight: 500,
                            }}
                          >
                            {s.label}
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-atelier-mute group-hover:text-atelier-coral transition-colors">
                            <span>使用此提示</span>
                            <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.8} />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-7">
                {messages.map((msg, idx) => {
                  const reloadHandler = msg.role === 'assistant' && !msg.isGreeting ? async () => {
                    let userMsgContent = null;
                    let userMsgIdx = -1;
                    for (let i = idx - 1; i >= 0; i--) {
                      if (messages[i].role === 'user') { userMsgContent = messages[i].content; userMsgIdx = i; break; }
                    }
                    if (!userMsgContent || !currentConversation?.id) return;
                    try {
                      await axios.post(`${API_URL}/api/chat/conversations/${currentConversation.id}/truncate`, { keep_count: userMsgIdx });
                    } catch {}
                    setMessages(prev => prev.slice(0, userMsgIdx));
                    sendMessageWithContent(userMsgContent);
                  } : undefined;

                  return (
                    <div key={idx} className="animate-fade-in">
                      <MessageBubble
                        msg={msg}
                        loading={loading || currentConversationIsGenerating}
                        onReload={reloadHandler}
                      />
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        <ChatInput
          input={input} setInput={setInput} loading={loading} disabled={currentConversationIsGenerating}
          subagents={subagents} textareaRef={textareaRef}
          showMentionDropdown={showMentionDropdown} setShowMentionDropdown={setShowMentionDropdown}
          mentionFilter={mentionFilter} setMentionFilter={setMentionFilter}
          mentionIndex={mentionIndex} setMentionIndex={setMentionIndex}
          onSend={sendMessage} onStop={stopGeneration} selectedAgent={selectedAgent}
          enableUltra={enableUltra} setEnableUltra={setEnableUltra}
          messages={messages} user={user}
        />
      </div>

      {showTokenLimitDialog && (
        <TokenLimitDialog onClose={() => setShowTokenLimitDialog(false)} onNewConversation={() => { setShowTokenLimitDialog(false); startNewConversation(); }} />
      )}
      {showUsageGuide && selectedAgent?.usage_guide && (
        <UsageGuideModal agent={selectedAgent} onClose={() => setShowUsageGuide(false)} />
      )}
      <FeedbackButton />
      <CommandPalette
        agents={agents}
        onSelectAgent={(a) => handleAgentChange(a.id)}
      />
    </div>
  );
};

export default Create;
